import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DressState {
  // Current dress
  selectedDress: string;
  selectedBackground: string;
  
  // Unlocked items
  dressUnlocks: Record<string, boolean>; // dress id -> unlocked status
  backgroundUnlocks: Record<string, boolean>; // background id -> unlocked status
  
  // Live2D settings
  live2dModel: string;
  live2dScale: number;
  live2dPositionX: number;
  live2dPositionY: number;
  
  // Actions
  selectDress: (dressId: string) => void;
  selectBackground: (backgroundId: string) => void;
  unlockDress: (dressId: string) => boolean;
  unlockBackground: (backgroundId: string) => boolean;
  updateLive2dSettings: (settings: Partial<{
    model: string;
    scale: number;
    positionX: number;
    positionY: number;
  }>) => void;
}

export const useDressStore = create<DressState>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedDress: 'default',
      selectedBackground: 'default',
      dressUnlocks: {
        'default': true  // default dress is unlocked
      },
      backgroundUnlocks: { default: true }, // default background is unlocked
      live2dModel: 'sora_casual',
      live2dScale: 0.5,
      live2dPositionX: 0,
      live2dPositionY: -100,

      // Actions
      selectDress: (dressId) =>
        set({ selectedDress: dressId }),

      selectBackground: (backgroundId) =>
        set({ selectedBackground: backgroundId }),

      unlockDress: (dressId) => {
        const { dressUnlocks } = get();
        if (!dressUnlocks[dressId]) {
          set((state) => ({
            dressUnlocks: {
              ...state.dressUnlocks,
              [dressId]: true,
            },
          }));
          return true;
        }
        return false;
      },

      unlockBackground: (backgroundId) => {
        const { backgroundUnlocks } = get();
        if (!backgroundUnlocks[backgroundId]) {
          set((state) => ({
            backgroundUnlocks: {
              ...state.backgroundUnlocks,
              [backgroundId]: true,
            },
          }));
          return true;
        }
        return false;
      },

      updateLive2dSettings: (settings) =>
        set((state) => ({
          live2dModel: settings.model ?? state.live2dModel,
          live2dScale: settings.scale ?? state.live2dScale,
          live2dPositionX: settings.positionX ?? state.live2dPositionX,
          live2dPositionY: settings.positionY ?? state.live2dPositionY,
        })),
    }),
    {
      name: 'sorarium-dress-storage',
      partialize: (state) => ({
        selectedDress: state.selectedDress,
        selectedBackground: state.selectedBackground,
        dressUnlocks: state.dressUnlocks,
        backgroundUnlocks: state.backgroundUnlocks,
        live2dModel: state.live2dModel,
        live2dScale: state.live2dScale,
        live2dPositionX: state.live2dPositionX,
        live2dPositionY: state.live2dPositionY,
      }),
    }
  )
);